import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function ResetPassword() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await axios.post(`${API_URL}/auth/password-reset/validate/`, { token });
        setTokenValid(response.data.valid);
      } catch {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== passwordConfirm) {
      setError(t('passwordReset.passwordsMismatch'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/password-reset/confirm/`, {
        token,
        new_password: password,
      });
      setResetDone(true);
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        const messages = [];
        for (const [, value] of Object.entries(data)) {
          const vals = Array.isArray(value) ? value : [value];
          messages.push(...vals.map(v => (typeof v === 'string' ? v : JSON.stringify(v))));
        }
        setError(messages.join(' '));
      } else {
        setError(t('passwordReset.invalidToken'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50 p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg mb-4">
            <TrendingUp className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Carlo</h1>
          <p className="text-sm text-slate-500 mt-1">{t('passwordReset.resetSubtitle')}</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-6">{t('passwordReset.resetTitle')}</h2>

          {validating ? (
            <div className="text-center text-sm text-slate-500 py-4">
              {t('register.invite.validating')}
            </div>
          ) : !tokenValid ? (
            <div className="space-y-4">
              <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg border border-red-200">
                {t('passwordReset.invalidToken')}
              </div>
              <Link
                to="/login"
                className="block w-full py-2.5 text-center bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg text-sm hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
              >
                {t('passwordReset.goToLogin')}
              </Link>
            </div>
          ) : resetDone ? (
            <div className="space-y-4">
              <div className="bg-green-50 text-green-700 text-sm px-3 py-3 rounded-lg border border-green-200">
                {t('passwordReset.resetSuccess')}
              </div>
              <Link
                to="/login"
                className="block w-full py-2.5 text-center bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg text-sm hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
              >
                {t('passwordReset.goToLogin')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('passwordReset.newPassword')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  autoFocus
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('passwordReset.confirmPassword')}
                </label>
                <input
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg border border-red-200">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg text-sm hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {loading ? t('passwordReset.resetting') : t('passwordReset.resetPassword')}
              </button>
            </form>
          )}

          <p className="mt-4 text-center text-sm text-slate-500">
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
              {t('passwordReset.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
